/**
 * API Client
 * 
 * This client handles all communication with the Labourando API backend.
 * It automatically attaches Cognito tokens via AuthService.
 */
import { getAccessToken } from './services/AuthService';
import posthog from 'posthog-js';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

class ApiClient {
  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  /**
   * Make an authenticated API request
   */
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const method = (options.method || 'GET').toUpperCase();
    const headers = {
      ...options.headers,
    };

    if (options.body != null && !headers['Content-Type'] && !headers['content-type']) {
      headers['Content-Type'] = 'application/json';
    }

    // Get auth token from AuthService
    try {
      const token = await getAccessToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    } catch (error) {
      // Token not available - proceed without auth (for public endpoints)
      console.debug('No auth token available:', error.message);
    }

    const response = await fetch(url, {
      ...options,
      method,
      headers,
    });

    // Handle errors
    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      const errorMessage = errorBody.message || errorBody.error || `API Error: ${response.status}`;
      const error = new Error(errorMessage);
      error.status = response.status;
      error.body = errorBody;

      // Track API errors in PostHog
      posthog?.capture('api_error', {
        endpoint,
        method,
        status: response.status,
        error_message: errorMessage,
        is_rate_limited: response.status === 429,
      });

      throw error;
    }

    // Return parsed JSON
    return response.json();
  }

  // Convenience methods
  get(endpoint) {
    return this.request(endpoint, { method: 'GET' });
  }

  post(endpoint, data) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  put(endpoint, data) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  patch(endpoint, data) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }

  /**
   * Make an authenticated request that returns raw text (e.g. CSV)
   */
  async getText(endpoint) {
    const url = `${this.baseUrl}${endpoint}`;
    const headers = {};
    try {
      const token = await getAccessToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
    } catch (_) { /* no token */ }
    const response = await fetch(url, { method: 'GET', headers, credentials: 'include' });
    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      const errorMessage = errorBody.message || errorBody.error || `API Error: ${response.status}`;
      const error = new Error(errorMessage);
      error.status = response.status;
      error.body = errorBody;

      // Track API errors in PostHog
      posthog?.capture('api_error', {
        endpoint,
        method: 'GET',
        status: response.status,
        error_message: errorMessage,
        is_rate_limited: response.status === 429,
      });

      throw error;
    }
    return response.text();
  }
}

// Singleton instance
const apiClient = new ApiClient();

export default apiClient;
